import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/ui/select';

/**
 * Choix puis confirmation d'une action appliquée à N éléments — pour TOUTES les zones
 * admin (actifs, clients, comptes, alertes, pilotage).
 *
 * Généralisé depuis le dialogue de pilotage groupé, qui était le seul écran à dire ce
 * qu'il allait faire avant de le faire. Partout ailleurs, une suppression irréversible
 * se confirmait dans un `window.confirm()` du navigateur : même poids visuel qu'un
 * « êtes-vous sûr ? » de 1998, aucun moyen de nommer la cible ni d'avertir.
 *
 * Ce composant ne connaît aucune action : il rend la carte qu'on lui passe. Chaque zone
 * déclare la sienne dans son `bulkActions.js`.
 *
 * Forme d'une action :
 *   label        libellé affiché
 *   family       'action' (rien à saisir) · 'value' (nombre) · 'select' (liste) · 'form' (champs libres)
 *   run          (ids, value) => Promise<rapport>   ce qui part réellement à l'API
 *   tone         'danger' pour teindre le bouton de confirmation
 *   warning      texte de l'encart ambre (sinon un texte générique)
 *   unavailable  motif — l'action reste listée mais grisée
 *   integration  'simulated' => encart « sans effet sur l'équipement »
 *   scope        (selectedItems) => { ids, note }  quand l'action ne vise qu'un sous-ensemble
 *   alwaysReport ouvre le compte rendu même si tout réussit
 *   options      [{ value, label }]  (family 'select')
 *   value_unit / value_min / value_max / default_value   (family 'value')
 *   renderForm   (value, setValue) => node   (family 'form')
 *
 * @param {object}   actions       { code: définition }
 * @param {array}    selectedItems éléments cochés, passés tels quels à `scope`
 * @param {node}     blockedReason si fourni, remplace le formulaire par une explication
 * @param {function} onSubmit      (code, value, definition) => void
 */
export default function BulkEditDialog({
  open, title = 'Édition groupée', noun = 'élément', count, actions = {},
  selectedItems = [], blockedReason = null, busy, onCancel, onSubmit,
}) {
  const [code, setCode] = useState('');
  const [value, setValue] = useState('');

  // Chaque ouverture repart d'une ardoise vierge : rouvrir le dialogue après une
  // suppression ne doit pas re-proposer « Supprimer » déjà armé.
  useEffect(() => {
    if (open) { setCode(''); setValue(''); }
  }, [open]);

  const def = actions[code];

  // Valeur par défaut au changement d'action — le champ montre toujours ce qui partira.
  useEffect(() => {
    if (!def) { setValue(''); return; }
    if (def.family === 'value') setValue(def.default_value != null ? String(def.default_value) : '');
    else if (def.family === 'select') setValue(def.default_value ?? '');
    else if (def.family === 'form') setValue(def.default_value ?? {});
    else setValue('');
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const scope = useMemo(
    () => (def?.scope ? def.scope(selectedItems) : null), [def, selectedItems]);
  const targetCount = scope ? scope.ids.length : count;

  const missingValue = def?.family === 'value'
    ? value === '' || Number.isNaN(parseFloat(value))
    : def?.family === 'select'
      ? !value
      : false;

  const disabled = busy || !!blockedReason || !def || !!def.unavailable
    || missingValue || targetCount === 0;

  const submit = () => {
    const payload = def.family === 'value' ? parseFloat(value)
      : def.family === 'action' ? null
        : value;
    onSubmit(code, payload, def);
  };

  return (
    <Dialog open={!!open} onOpenChange={o => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle>
            {title} — {count} {noun}{count > 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-[13px]">
          {blockedReason ? (
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100 text-[12px] text-amber-800"
              data-testid="bulk-blocked"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{blockedReason}</span>
            </div>
          ) : (
            <p className="text-neutral-500">
              Sélectionnez l'action à appliquer à{' '}
              <strong className="text-neutral-900">{count}</strong> {noun}{count > 1 ? 's' : ''}.
            </p>
          )}

          <div hidden={!!blockedReason}>
            <Label className="text-[11px] text-neutral-500">Action</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger className="h-9" data-testid="bulk-action-select">
                <SelectValue placeholder="Choisir une action…" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(actions).map(([c, d]) => (
                  <SelectItem key={c} value={c} disabled={!!d.unavailable}>
                    {d.label}{d.integration === 'simulated' ? ' (simulé)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {def?.unavailable && (
            <p className="text-[11px] text-amber-700" data-testid="bulk-unavailable">
              {def.unavailable}
            </p>
          )}

          {/* Ce que l'action va réellement toucher, quand ce n'est pas toute la sélection.
              Le filtrage par type existait déjà, mais silencieusement : on cochait douze
              actifs et quatre partaient, sans que l'écran le dise nulle part. */}
          {scope && (
            <div
              className="p-3 rounded-md bg-neutral-50 border border-neutral-100 text-[12px]"
              data-testid="bulk-scope"
            >
              <p className="text-neutral-400 text-[11px]">Sélection : {count} {noun}{count > 1 ? 's' : ''}</p>
              <p className={`font-medium ${targetCount ? 'text-neutral-900' : 'text-amber-700'}`}>
                {targetCount === 0 ? 'Aucun élément concerné par cette action' : scope.note}
              </p>
            </div>
          )}

          {def?.family === 'value' && (
            <div>
              <Label className="text-[11px] text-neutral-500">
                Valeur ({def.value_unit})
                {def.value_min != null && (
                  <span className="text-[10px] text-neutral-400 ml-1">
                    ({def.value_min} – {def.value_max})
                  </span>
                )}
              </Label>
              <Input
                type="number" className="h-9" value={value}
                min={def.value_min} max={def.value_max}
                onChange={e => setValue(e.target.value)}
                data-testid="bulk-value-input"
              />
            </div>
          )}

          {def?.family === 'select' && (
            <div>
              <Label className="text-[11px] text-neutral-500">{def.value_label || 'Valeur'}</Label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger className="h-9" data-testid="bulk-value-select">
                  <SelectValue placeholder={def.value_placeholder || 'Choisir…'} />
                </SelectTrigger>
                <SelectContent>
                  {(def.options || []).map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {def?.family === 'form' && def.renderForm?.(value || {}, setValue)}

          {def?.integration === 'simulated' && (
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-neutral-100 border border-neutral-200 text-[12px] text-neutral-700"
              data-testid="simulated-notice"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500" />
              <span>
                Aucune intégration n'est branchée pour cette action : elle sera enregistrée
                dans le journal mais <strong>n'aura aucun effet sur l'équipement</strong>.
              </span>
            </div>
          )}

          {!blockedReason && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100 text-[12px] text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>
                {def?.warning || (
                  <>
                    Cette action ne peut pas être annulée. Chacune des {targetCount} exécutions
                    sera tracée dans le journal avec votre nom et l'horodatage.
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="bulk-cancel-btn">Annuler</Button>
          <Button
            variant={def?.tone === 'danger' ? 'destructive' : 'default'}
            disabled={disabled}
            onClick={submit}
            data-testid="bulk-confirm-btn"
          >
            Confirmer et exécuter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
